import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '@structural-climbing/auth'
import type { Product, ProductCategory } from './types'

interface ProductsResponse {
  categories: ProductCategory[]
  products: Product[]
  currency: string
}

/**
 * The gym's catalogue. `reload` refetches without the spinner, so a sale or an
 * edit updates stock on the tiles without the grid blinking out.
 */
export function useProducts() {
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [data, setData] = useState<ProductsResponse>({
    categories: [],
    products: [],
    currency: 'RON',
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const reload = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/products`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        setError(true)
        return
      }
      setData((await res.json()) as ProductsResponse)
      setError(false)
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [apiUrl, getAccessTokenSilently])

  useEffect(() => {
    void reload()
  }, [reload])

  const productsById = useMemo(
    () => new Map(data.products.map((product) => [product.id, product])),
    [data.products],
  )

  return { ...data, productsById, loading, error, reload }
}
